import ReactECharts from 'echarts-for-react';
import { motion } from 'framer-motion';

interface PieChart3DProps {
  data: Array<{ name: string; value: number; fill: string }>;
  title?: string;
  height?: number;
}

export function PieChart3D({ data, title, height = 300 }: PieChart3DProps) {
  if (!data || data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        No hay datos disponibles
      </div>
    );
  }

  // Calculate total for percentages
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  const option = {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)',
      backgroundColor: 'hsl(var(--popover))',
      borderColor: 'hsl(var(--border))',
      textStyle: {
        color: 'hsl(var(--popover-foreground))'
      }
    },
    legend: {
      orient: 'horizontal',
      bottom: '0%',
      textStyle: {
        color: 'hsl(var(--foreground))'
      },
      formatter: (name: string) => {
        const item = data.find(d => d.name === name);
        if (item && total > 0) {
          const percentage = ((item.value / total) * 100).toFixed(0);
          return `${name}: ${percentage}%`;
        }
        return name;
      }
    },
    series: [
      {
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['50%', '40%'],
        avoidLabelOverlap: true,
        label: {
          show: true,
          position: 'outside',
          formatter: '{b}: {c}',
          fontSize: 12,
          lineHeight: 16,
          overflow: 'break',
          color: 'inherit'
        },
        labelLine: {
          show: true,
          length: 15,
          length2: 10,
          smooth: true
        },
        data: data.map(item => ({
          value: item.value,
          name: item.name,
          itemStyle: {
            color: item.fill
          },
          emphasis: {
            itemStyle: {
              color: item.fill,
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            },
            scale: true,
            scaleSize: 10
          }
        })),
        animationType: 'scale',
        animationEasing: 'elasticOut',
        animationDelay: (idx: number) => idx * 100
      }
    ],
    animationDuration: 800
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      {title && (
        <h3 className="mb-4 text-lg font-semibold text-foreground">{title}</h3>
      )}
      <ReactECharts option={option} style={{ height }} />
    </motion.div>
  );
}
